import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ClipboardCheck, CreditCard, GraduationCap, HandCoins, QrCode } from "lucide-react";
import { SiteHeader, SiteFooter, Spinner } from "@/components/Layout";
import { TaglineText } from "@/components/Brand";
import { pub } from "@/lib/api";

const PERKS = [
  [HandCoins, "No activation fee", "The one-time activation fee is waived for pilot venues."],
  [GraduationCap, "Practice mode", "Train your team with test orders that never charge anyone or show in your reports."],
  [CreditCard, "Card payments with Stripe", "Customers pay by card, Apple Pay or Google Pay and the money goes straight to your Stripe account."],
  [ClipboardCheck, "Or manual approval, pay at pickup", "Don't have Stripe yet? Approve each order and take payment at the counter by cash, card terminal or e-Transfer."],
  [QrCode, "Your own QR sign", "Print your QR sign and customers order from their phone without installing an app."],
];
const STEPS = ["Create your free account", "Add your venue, menu and staff", "Try a practice order", "LineGone approves you and real orders open"];

export default function Pilot() {
  const nav = useNavigate();
  const [open, setOpen] = useState(null);
  useEffect(() => {
    document.title = "Join the LineGone pilot · LineGone";
    pub.get("/pilot").then((r) => setOpen(r.data.open)).catch(() => setOpen(false));
  }, []);
  const join = () => { localStorage.setItem("lg_signup_source", "pilot_link"); nav("/sign-up"); };
  if (open === null) return <Spinner />;
  return (
    <div className="min-h-screen bg-white" data-testid="pilot-page">
      <SiteHeader right={<Link to="/sign-in" className="link-lg whitespace-nowrap text-sm" data-testid="pilot-signin-link">Sign in</Link>} />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-16">
        <div className="eyebrow">LineGone pilot program · Canada</div>
        <h1 className="mt-2 text-4xl font-black leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="pilot-title">Set up your venue as a LineGone pilot</h1>
        <p className="mt-4 max-w-2xl text-base text-ink/80 md:text-lg"><TaglineText /> Cafés, food trucks and event organisers can join for free and start taking orders without a line.</p>
        {open ? (
          <button className="btn-primary mt-8 !px-8 !py-4 text-lg" onClick={join} data-testid="pilot-join-btn">Join the pilot</button>
        ) : (
          <div className="mt-8 max-w-xl rounded-2xl bg-amber-100 p-5 text-amber-900" data-testid="pilot-closed">
            <div className="font-bold">The pilot isn't taking new venues right now.</div>
            <p className="mt-1 text-sm">You can still sign up as a regular LineGone venue.</p>
            <Link to="/sign-up" className="btn-primary mt-3 inline-flex !py-2" data-testid="pilot-regular-signup-btn">Sign up as a venue</Link>
          </div>
        )}
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PERKS.map(([I, t, d]) => (
            <div key={t} className="card-lg p-5" data-testid="pilot-perk">
              <I size={22} className="text-brand" /><h3 className="mt-3 font-heading text-lg font-extrabold">{t}</h3><p className="mt-1 text-sm text-muted2">{d}</p>
            </div>
          ))}
        </div>
        <h2 className="mt-14 text-base font-black md:text-lg">How it works</h2>
        <ol className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => <li key={s} className="flex items-start gap-3 rounded-2xl bg-brand-pale p-4 text-sm font-semibold"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-white">{i + 1}</span>{s}</li>)}
        </ol>
        <p className="mt-6 flex items-center gap-2 text-sm text-muted2"><CheckCircle2 size={16} className="text-brand" /> Every pilot venue is reviewed by LineGone before real customer orders open. Prices in Canadian dollars.</p>
        {open && <button className="btn-primary mt-8" onClick={join} data-testid="pilot-join-btn-bottom">Join the pilot</button>}
      </main>
      <SiteFooter />
    </div>
  );
}
